import PageHeader from '@src/components/PageHeader/PageHeader';
import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useAppSelector } from '@src/store/hooks';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';
import { CustomAlertType } from '@src/interfaces/type';
import CustomAlert from '@src/components/CustomAlert/CustomAlert';
import { Alert, Snackbar } from '@mui/material';
import { RESTAURANT } from '@src/Constants';
import AcceptDelivery from './acceptDelivery';
import { getQueryParamValue } from '@src/helpers/Helpers';
import Orders from './Orders';
import OrderMobileHomePage from './OrderMobileHomePage';
import { Order } from '@src/components/Main/Chat/ChatMessage/ChatMessage';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	transition: width 0.3s ease;
`;

export type OrderView = 'orderDetails' | 'qualityCheck' | 'requestSupplier' | 'productDetails' | '';

export const orderViews = ['orderDetails', 'qualityCheck', 'requestSupplier'];

const OrderManagement = () => {
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const [isErrorVisible, setErrorVisible] = useState(false);
	const [alert, setAlert] = useState<CustomAlertType>({ message: '', severity: 'success' });
	const [errorMessage, setErrorMessage] = useState('');
	const [view, setView] = useState<OrderView>('');
	const [viewOrder, setViewOrder] = useState(false);
	const [cartData, setCartData] = useState([]);
	const [filteredRows, setFilteredRows] = useState<Order[]>([]);
	const [searchValue, setSearchValue] = useState<string>('');
	const [orders, setOrders] = useState<Order[]>([]);
	const [collapse, setCollapse] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();
	const { id, userId } = useParams();
	const { isDrawerOpen } = useAppSelector((state) => state?.UI);
	const isMobileWidth = IsMobileWidth();

	const pageContainerStyles: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		transition: 'width 0.3s ease',
		width:
			isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 240px)'
				: !isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 64px)'
				: '100%',
	};

	const pageTitle = (path: string): string => {
		const page: string | undefined = path?.split('/')?.at(-1);
		const titles: Record<string, string> = {
			allOrders: 'All Orders',
			acceptDelivery: 'Accept Delivery',
		};
		if (page && titles[page]) {
			return titles[page];
		}
		return 'All Orders';
	};

	const displayCustomError = (error: string) => {
		setErrorMessage(error);
		setErrorVisible(true);
	};

	const handleArrowBack = () => {
		navigate(-1);
	};

	const activeBackIcon = (path: string) => {
		return false;
	};
	const handleErrorClose = () => {
		setErrorMessage('');
		setErrorVisible(false);
	};

	const orderMobileRoutes = (path: string, id: string | undefined, userId: string | undefined) => {
		return (
			<OrderMobileHomePage
				view={view}
				setView={setView}
				viewOrder={viewOrder}
				setViewOrder={setViewOrder}
				filteredRows={filteredRows}
				setFilteredRows={setFilteredRows}
				setSearchValue={setSearchValue}
				searchValue={searchValue}
				orders={orders}
				setOrders={setOrders}
				collapse={collapse}
				setCollapse={setCollapse}
			/>
		);
	};

	const orderRoutes = (path: string, id: string | undefined, userId: string | undefined) => {
		switch (path) {
			case `/suppliers/${id}/orders/allOrders`:
			case `/restaurants/${id}/orders/allOrders`:
				return (
					<Orders
						filteredRows={filteredRows}
						setFilteredRows={setFilteredRows}
						setSearchValue={setSearchValue}
						searchValue={searchValue}
						orders={orders}
						setOrders={setOrders}
						collapse={collapse}
					/>
				);
			case `/suppliers/${id}/orders/acceptDelivery`:
			case `/restaurants/${id}/orders/acceptDelivery`:
				return (
					<AcceptDelivery
						view={view}
						setView={setView}
						viewOrder={viewOrder}
						setViewOrder={setViewOrder}
						filteredRows={filteredRows}
						setFilteredRows={setFilteredRows}
						setSearchValue={setSearchValue}
						searchValue={searchValue}
						orders={orders}
						setOrders={setOrders}
						collapse={collapse}
					/>
				);

			default:
				return (
					<Orders
						filteredRows={filteredRows}
						setFilteredRows={setFilteredRows}
						setSearchValue={setSearchValue}
						searchValue={searchValue}
						orders={orders}
						setOrders={setOrders}
						collapse={collapse}
					/>
				);
		}
	};

	return (
		<StyledContainer style={pageContainerStyles}>
			{!isMobileWidth ? (
				<PageHeader
					title={pageTitle(location?.pathname)}
					activeIcon={activeBackIcon(view)}
					handleArrowBack={handleArrowBack}
				/>
			) : null}
			{isMobileWidth
				? orderMobileRoutes(location?.pathname, id, userId)
				: orderRoutes(location?.pathname, id, userId)}
			{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				open={isErrorVisible}
				autoHideDuration={6000}
				onClose={handleErrorClose}
			>
				<Alert onClose={handleErrorClose} severity="error" elevation={4}>
					{errorMessage}
				</Alert>
			</Snackbar>
		</StyledContainer>
	);
};

export default OrderManagement;
